import { Skeleton } from 'antd';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { BodyXS } from '../../styles/Typography';

type SummaryTableRowProps = {
  description: string;
  strikethroughPrice?: string;
  price?: string;
  bold?: boolean;
  badge?: boolean;
  smallerFont?: boolean;
  halfOpacity?: boolean;
  asterisk?: boolean;
  dataTest?: string;
  subtitle?: string;
};

export const SummaryTableRow: FunctionComponent<SummaryTableRowProps> = (props) => {
  const {
    description,
    strikethroughPrice,
    price,
    bold = false,
    smallerFont = false,
    halfOpacity = false,
    badge = false,
    asterisk = false,
    dataTest,
    subtitle,
  } = props;

  return price === undefined ? (
    <Skeleton title={{ width: '100%' }} paragraph={false} active />
  ) : (
    <PriceRow data-test={dataTest}>
      <div>
        <BalancedP bold={bold}>{description}</BalancedP>
        {subtitle !== undefined && <BalancedP smallerFont={true}>{subtitle}</BalancedP>}
      </div>
      <BalancedP smallerFont={smallerFont} halfOpacity={halfOpacity}>
        {strikethroughPrice !== undefined && (
          <FadedSlash data-test="strikethroughPrice">{strikethroughPrice}</FadedSlash>
        )}
        {asterisk && <Asterisk>*</Asterisk>}
        <span data-test="price">{badge ? <Badge>{price}</Badge> : price}</span>
      </BalancedP>
    </PriceRow>
  );
};

type BalancedPProps = { smallerFont?: boolean; halfOpacity?: boolean };
const BalancedP = styled(BodyXS)<BalancedPProps>`
  opacity: ${({ halfOpacity }) => {
    if (!halfOpacity) return 1;
    return appOverride({ nerva: 0.8, default: 0.5 });
  }};
  margin-bottom: 0.5em;
  margin-top: 0.5em;
  font-size: ${({ smallerFont, theme }) => (smallerFont ? theme.fontSize.xxs : theme.fontSize.xs)};
`;

const FadedSlash = styled.span`
  text-decoration: line-through;
  opacity: ${appOverride({ nerva: 0.8, default: 0.5 })};
  margin: ${({ theme }) => `0 ${theme.spacing._6px}`};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const Asterisk = styled.span`
  opacity: 0.5;
  text-decoration: none !important;
  margin-left: -6px;
  margin-right: 6px;
`;

const Badge = styled.span`
  background-color: ${Color.decorative.onAccent};
  color: ${appOverride({
    nerva: Color.content.accent,
    default: Color.surface.default,
  })};
  padding: ${({ theme }) => `${theme.spacing._6px} ${theme.spacing._18px}`};
  border-radius: 20px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  white-space: pre-line; /* For line break \n in strings */
`;
